import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import Text from 'modules/core/components/Inputs/Text'
import Popover from 'modules/core/components/Popover'
import SimpleUserSelector from 'modules/core/components/SimpleUserSelector'
import { GetUserExtraInformationFn } from 'modules/core/components/UserSelector/List/Item'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import Users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

interface Props {
  label: string
  description?: string
  none?: boolean
  users: User[]
  onSubmit?: (arg0: number[]) => void
  onGetUserExtraInformation?: GetUserExtraInformationFn
  selectedIds: number[]
  disabledUsers?: number[]
  multiselect?: boolean
  readonly?: boolean
  allowsEmpty?: boolean
  applyDefaultFilters?: boolean
  placeHolder?: string
  submitEmptyLabel?: string
  applySubmitLabelWithUsername?: boolean
  outsideCancel?: boolean
  showControls?: boolean
}

// TODO: This is very similar to LeaveTypeSelectorInput
export const UserSelectorInput = observer(
  ({
    label,
    description,
    selectedIds,
    users,
    disabledUsers = [],
    onSubmit,
    onGetUserExtraInformation,
    multiselect = true,
    readonly = false,
    allowsEmpty = false,
    placeHolder = i18n.t('user_selector.label.zero'),
    submitEmptyLabel = i18n.t('user_selector.submit.nobookkeeper'),
    applySubmitLabelWithUsername = false,
    outsideCancel = false,
    showControls = true,
  }: Props) => {
    const [ids, setIds] = React.useState(selectedIds)
    const [focused, setFocused] = React.useState(false)
    const count = ids.length
    const initialIds = selectedIds

    // Allow to change selectedIds from the container
    useUnsafeEffect(() => {
      setIds(selectedIds)
    }, [selectedIds])

    const userModel = Users.find({ id: ids[0] })
    const user = userModel
      ? getFullDisplayName({
          firstName: userModel.attributes.first_name,
          lastName: userModel.attributes.last_name,
        })
      : undefined

    const textLabel = count
      ? i18n.tp('user_selector.label', {
          count,
          user,
        })
      : placeHolder

    const submitLabel = count
      ? applySubmitLabelWithUsername
        ? i18n.t('user_selector.submit.single_user', {
            user,
          })
        : i18n.tp('user_selector.submit', {
            count,
          })
      : allowsEmpty
        ? submitEmptyLabel
        : i18n.t('user_selector.submit.zero')

    const onSubmited = () => {
      if (onSubmit) onSubmit(ids)

      setFocused(false)
    }

    const onOutsideClick = () => {
      outsideCancel && setIds(initialIds)
    }

    const handleSelect = (id: number | null | undefined) => {
      const newIds =
        !multiselect && id && !ids.includes(id)
          ? [id].filter((id: number | undefined): id is number => Boolean(id))
          : xor(
              ids,
              [id].filter((id: number | undefined | null): id is number => Boolean(id))
            )
      setIds(newIds)
    }

    return (
      <Popover
        overflowContent
        attachment="top center"
        open={focused}
        size="bigger"
        toggle={(val) => () => !readonly && setFocused(val)}
        onOutsideClick={onOutsideClick}
      >
        <Text
          label={label}
          description={description}
          readonly={readonly}
          field={new Field(textLabel, 'string')}
          select
        />
        <Box padding="s32" gap="s24">
          <SimpleUserSelector
            loadingOptions={false}
            users={users}
            onSelect={handleSelect}
            onSelectMultiple={multiselect ? setIds : null}
            selectedIds={ids}
            showControls={showControls}
            disabledUsers={disabledUsers}
            onGetUserExtraInformation={onGetUserExtraInformation}
          />
          <Button onClick={onSubmited} disabled={!count && !allowsEmpty}>
            {submitLabel}
          </Button>
        </Box>
      </Popover>
    )
  }
)
